import { Helmet } from "react-helmet";

interface BlogPostSEOProps {
  title: string;
  excerpt: string;
  content: string;
  slug: string;
  category: string;
  authorName: string;
  publishedAt: string;
  updatedAt?: string;
  featuredImage?: string;
  tags?: string[];
}

export const BlogPostSEO = ({
  title,
  excerpt,
  content,
  slug,
  category,
  authorName,
  publishedAt,
  updatedAt,
  featuredImage,
  tags = [],
}: BlogPostSEOProps) => {
  const canonicalUrl = `https://spotlightadz.ma/blog/${slug}`;
  const image = featuredImage || "https://spotlightadz.ma/logo.png";
  
  // Calculate reading time
  const wordCount = content.split(/\s+/).length;
  const readingTime = Math.ceil(wordCount / 200);

  // Article Schema
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": `${canonicalUrl}#article`,
    headline: title,
    description: excerpt,
    image: {
      "@type": "ImageObject",
      url: image,
      width: 1200,
      height: 630,
    },
    author: {
      "@type": "Person",
      name: authorName,
      url: "https://spotlightadz.ma",
    },
    publisher: {
      "@type": "Organization",
      name: "SpotlightAdz",
      logo: {
        "@type": "ImageObject",
        url: "https://spotlightadz.ma/logo.png",
        width: 512,
        height: 512,
      },
    },
    datePublished: publishedAt,
    dateModified: updatedAt || publishedAt,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": canonicalUrl,
    },
    articleSection: category,
    keywords: tags.join(", "),
    wordCount: wordCount,
    timeRequired: `PT${readingTime}M`,
    inLanguage: "en",
    isAccessibleForFree: true,
    isPartOf: {
      "@type": "Blog",
      "@id": "https://spotlightadz.ma/blog#blog",
      name: "SpotlightAdz Blog",
      url: "https://spotlightadz.ma/blog",
    },
  };

  // BreadcrumbList Schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://spotlightadz.ma",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Blog",
        item: "https://spotlightadz.ma/blog",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: category,
        item: `https://spotlightadz.ma/blog?category=${encodeURIComponent(category)}`,
      },
      {
        "@type": "ListItem",
        position: 4,
        name: title,
        item: canonicalUrl,
      },
    ],
  };

  // FAQ Schema (if applicable)
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: `What is ${title} about?`,
        acceptedAnswer: {
          "@type": "Answer",
          text: excerpt,
        },
      },
    ],
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{`${title} | SpotlightAdz Blog`}</title>
      <meta name="title" content={`${title} | SpotlightAdz Blog`} />
      <meta name="description" content={excerpt} />
      <meta name="keywords" content={`${tags.join(", ")}, SpotlightAdz, digital marketing Morocco, ${category}`} />
      <meta name="author" content={authorName} />
      <meta name="robots" content="index, follow, max-image-preview:large" />

      {/* Canonical */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:type" content="article" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={excerpt} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="SpotlightAdz" />
      <meta property="article:published_time" content={publishedAt} />
      {updatedAt && <meta property="article:modified_time" content={updatedAt} />}
      <meta property="article:author" content={authorName} />
      <meta property="article:section" content={category} />
      {tags.map((tag) => (
        <meta key={tag} property="article:tag" content={tag} />
      ))}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={excerpt} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:label1" content="Written by" />
      <meta name="twitter:data1" content={authorName} />
      <meta name="twitter:label2" content="Reading time" />
      <meta name="twitter:data2" content={`${readingTime} min read`} />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(articleSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>
    </Helmet>
  );
};

export default BlogPostSEO;
